// frontend/src/components/Plan.js

import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useCallback } from "react";
import VetStartedSOAPSection from "./VetStartedSOAPSection";
import TreatmentPlanTab from "./TreatmentPlanTab";
import PrescriptionTab from "./PrescriptionTab";
import RecommendationsTab from "./RecommendationsTab";
import CertificatesTab from "./CertificatesTab";
import FollowupsTab from "./FollowupsTab";
import { usePrescription } from "./PrescriptionContext";

const PLAN_TABS = [
  { id: "treatment", label: "Services/Products" },
  { id: "prescription", label: "Prescription/Medication" },
  { id: "recommendations", label: "Recommendations" },
  { id: "certificates", label: "Certificates" },
  { id: "followups", label: "Follow-ups" },
];

const PlanTabs = ({ activeTab, onChange, counts }) => (
  <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 w-fit flex-wrap">
    {PLAN_TABS.map((tab) => {
      const isActive = tab.id === activeTab;
      const count = counts[tab.id] || 0;
      return (
        <button
          key={tab.id}
          type="button"
          data-nav-tab="true"
          onClick={() => onChange(tab.id)}
          className={`flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-semibold rounded-lg transition-colors ${
            isActive ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {tab.label}
          {count > 0 && (
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
              }`}
            >
              {count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const Plan = forwardRef(({
  defaultOpen = false,
  isExpanded = true,
  onToggle,
  onDirtyChange,
  initialData,
  refreshKey,
  onPlanChange,
  isHydratingRef,
}, ref) => {
  const [activeTab, setActiveTab] = useState("treatment");
  const [counts, setCounts] = useState({
    treatment: 0,
    prescription: 0,
    recommendations: 0,
    certificates: 0,
    followups: 0,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentInitialData, setCurrentInitialData] = useState(initialData);
  const prevRefreshKey = useRef(refreshKey);
  const isSyncing = useRef(false);
  const lastSyncedHash = useRef('');
  const isUpdatingCounts = useRef(false);
  
  const hasUnsavedChangesRef = useRef(false);

  const treatmentRef = useRef(null);
  const prescriptionRef = useRef(null);
  const recommendationsRef = useRef(null);
  const certificatesRef = useRef(null);
  const followupsRef = useRef(null);

  const { prescriptionRef: savedPrescriptionRefId, medicationsList, labTests, notes: prescriptionNotes } = usePrescription();

  // ─── Reset dirty state when loading saved data ──────────────────────────
  useEffect(() => {
    if (refreshKey !== prevRefreshKey.current && initialData) {
      if (hasUnsavedChangesRef.current) {
        return;
      }
      prevRefreshKey.current = refreshKey;
      setCurrentInitialData(initialData);
      hasUnsavedChangesRef.current = false;
      onDirtyChange?.(false);
    }
  }, [refreshKey, initialData, onDirtyChange]);

  // ─── Initial data load - reset dirty state ──────────────────────────────
  useEffect(() => {
    if (!initialData) {
      setIsInitialized(true);
      return;
    }

    if (hasUnsavedChangesRef.current) {
      return;
    }

    if (!isInitialized) {
      setCurrentInitialData(initialData);
      setIsInitialized(true);
      hasUnsavedChangesRef.current = false;
      onDirtyChange?.(false);
    }
  }, [initialData, isInitialized, onDirtyChange]);

  const handleSummaryChange = useCallback((tabId, summary) => {
    if (isUpdatingCounts.current) return;
    
    const newCount = summary?.itemCount || 0;
    
    setCounts(prev => {
      if (prev[tabId] === newCount) {
        return prev;
      }
      isUpdatingCounts.current = true;
      const newCounts = {
        ...prev,
        [tabId]: newCount,
      };
      setTimeout(() => {
        isUpdatingCounts.current = false;
      }, 0);
      return newCounts;
    });
  }, []);

  const handleTreatmentSummary = useCallback((summary) => {
    handleSummaryChange("treatment", summary);
  }, [handleSummaryChange]);

  const handlePrescriptionSummary = useCallback((summary) => {
    handleSummaryChange("prescription", summary);
  }, [handleSummaryChange]);

  const handleRecommendationsSummary = useCallback((summary) => {
    handleSummaryChange("recommendations", summary);
  }, [handleSummaryChange]);

  const handleCertificatesSummary = useCallback((summary) => {
    handleSummaryChange("certificates", summary);
  }, [handleSummaryChange]);

  const handleFollowupsSummary = useCallback((summary) => {
    handleSummaryChange("followups", summary);
  }, [handleSummaryChange]);

  // AFTER
  const markDirty = useCallback(() => {
    hasUnsavedChangesRef.current = true;
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  const resetDirty = useCallback(() => {
    hasUnsavedChangesRef.current = false;
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  useEffect(() => {
    if (isInitialized && !isUpdatingCounts.current) {
      handleSummaryChange("prescription", { itemCount: medicationsList.length });
    }
  }, [medicationsList.length, isInitialized, handleSummaryChange]);

  const getRecommendationsData = useCallback(() => {
    const recommendationsData = recommendationsRef.current?.getData() || {};
    return {
      quickRecommendations: recommendationsData.quickRecommendations || [],
      notes: recommendationsData.notes || "",
    };
  }, []);

  useImperativeHandle(ref, () => ({
    getData: () => {
      const treatmentData = treatmentRef.current?.getData() || {};
      const prescriptionFromTab = prescriptionRef.current?.getData() || { medications: [], notes: "" };
      const recommendationsData = recommendationsRef.current?.getData() || {};
      const certificatesData = certificatesRef.current?.getData() || {};
      const followupsData = followupsRef.current?.getData() || {};

      const contextMedications = medicationsList.filter(
        (m) => m.drugName?.trim() || m.product_id,
      );
      const prescriptionData = {
        medications:
          prescriptionFromTab.medications?.length > 0
            ? prescriptionFromTab.medications
            : contextMedications,
        notes: prescriptionFromTab.notes ?? prescriptionNotes ?? "",
      };

      // Get followUpData from FollowupsTab (array format)
      const followUpDataArray = followupsData.followUpData || [];
      
      // Return followUpData as array for backend
      const followUpData = followUpDataArray.map(f => ({
        date: f.date || "",
        purpose: f.purpose || "",
        status: f.status || "scheduled",
        notes: f.notes || "",
      }));

      let proceduresList = treatmentData.proceduresList || [];
      let planProducts = treatmentData.planProducts || [];

      return {
        diagnosticsOrdered: treatmentData.diagnosticsOrdered || "",
        plan: treatmentData.plan || "",
        proceduresList: proceduresList,
        planProducts: planProducts,
        appliedTreatmentPlan: treatmentData.appliedTreatmentPlan || null,
        medicationsList: prescriptionData.medications || [],
        labTests: labTests || [],
        recommendations: recommendationsData,
        certificates: certificatesData,
        followUpData: followUpData, // Now an array
        prescription: prescriptionData,
        prescriptionRef: savedPrescriptionRefId,
      };
    },
    getRecommendationsData,
    resetDirty,
    isDirty: () => hasUnsavedChangesRef.current,
    setInitialData: (data) => {
      if (!hasUnsavedChangesRef.current) {
        setCurrentInitialData(data);
      }
    }, 
    hasHydrated: () => treatmentRef.current?.hasHydrated?.() ?? true,
  }));

  const totalItemCount = Object.values(counts).reduce((a, b) => a + b, 0);

  // FIX: Tab content renderer with min-height to prevent scroll jump
  const renderTabContent = (tabId) => {
    const isActive = activeTab === tabId;
    const contentRefs = {
      treatment: treatmentRef,
      prescription: prescriptionRef,
      recommendations: recommendationsRef,
      certificates: certificatesRef,
      followups: followupsRef,
    };
    const summaryHandlers = {
      treatment: handleTreatmentSummary,
      prescription: handlePrescriptionSummary,
      recommendations: handleRecommendationsSummary,
      certificates: handleCertificatesSummary,
      followups: handleFollowupsSummary,
    };
    const components = {
      treatment: TreatmentPlanTab,
      prescription: PrescriptionTab,
      recommendations: RecommendationsTab,
      certificates: CertificatesTab,
      followups: FollowupsTab,
    };
    const initialDataMap = {
      treatment: currentInitialData,
      prescription: null,
      recommendations: currentInitialData?.recommendations,
      certificates: currentInitialData?.certificates,
      followups: currentInitialData?.followUpData || currentInitialData?.followups,
    };

    const Component = components[tabId];
    const ref = contentRefs[tabId];
    const onSummaryChange = summaryHandlers[tabId];
    const initialDataForTab = initialDataMap[tabId];

    return (
      <div 
        key={tabId}
        style={{ 
          display: isActive ? "block" : "none",
          minHeight: "200px",
        }}
      >
        <Component
          ref={ref}
          onDirtyChange={markDirty}
          onSummaryChange={onSummaryChange}
          initialData={initialDataForTab}
          planRef={ref}
        />
      </div>
    );
  };

  return (
    <VetStartedSOAPSection
      id="soap-section-P"
      letter="P"
      title="Plan"
      subtitle="Treatment, procedures & medications"
      accentBg="#f0fdf4"
      accentColor="#15803d"
      badge={totalItemCount > 0 ? `${totalItemCount} items` : null}
      defaultOpen={defaultOpen}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      <PlanTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        counts={counts}
      />

      {/* FIX: Render all tabs with min-height */}
      <div className="plan-tabs-content">
        {PLAN_TABS.map(tab => renderTabContent(tab.id))}
      </div>
    </VetStartedSOAPSection>
  );
});

Plan.displayName = "Plan";

export default Plan;